import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, File, Image, FileSpreadsheet, FileType, Download, Eye, Folder } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_DOCUMENTS = [
  { id: '1', name: 'Safety Policy 2024.pdf', type: 'pdf', size: 2450000, created_date: new Date(Date.now() - 3600000).toISOString(), project_name: 'Main Office' },
  { id: '2', name: 'Risk Assessment Template.docx', type: 'docx', size: 125000, created_date: new Date(Date.now() - 86400000).toISOString(), project_name: 'Site Alpha' },
  { id: '3', name: 'Training Schedule.xlsx', type: 'xlsx', size: 340000, created_date: new Date(Date.now() - 172800000).toISOString(), project_name: 'All Spaces' },
  { id: '4', name: 'Site Photo.jpg', type: 'jpg', size: 4500000, created_date: new Date(Date.now() - 259200000).toISOString(), project_name: 'Warehouse B' },
  { id: '5', name: 'Compliance Report.pdf', type: 'pdf', size: 1800000, created_date: new Date(Date.now() - 432000000).toISOString(), project_name: 'Distribution Center' },
];

const getFileIcon = (type) => {
  const icons = {
    pdf: { icon: FileText, color: 'text-red-500 bg-red-100' },
    docx: { icon: FileType, color: 'text-blue-500 bg-blue-100' },
    doc: { icon: FileType, color: 'text-blue-500 bg-blue-100' },
    xlsx: { icon: FileSpreadsheet, color: 'text-green-500 bg-green-100' },
    xls: { icon: FileSpreadsheet, color: 'text-green-500 bg-green-100' },
    jpg: { icon: Image, color: 'text-purple-500 bg-purple-100' },
    jpeg: { icon: Image, color: 'text-purple-500 bg-purple-100' },
    png: { icon: Image, color: 'text-purple-500 bg-purple-100' },
  };
  return icons[type?.toLowerCase()] || { icon: File, color: 'text-gray-500 bg-gray-100' };
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function DocumentsRecentTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setDocuments(MOCK_DOCUMENTS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadDocuments = async () => {
      try {
        setLoading(true);

        // Load recent documents from storage or documents table
        const { data, error } = await supabase
          .from('project_documents')
          .select(`
            id,
            name,
            file_type,
            file_size,
            created_date,
            projects!inner(name, company_id)
          `)
          .eq('projects.company_id', company.id)
          .order('created_date', { ascending: false })
          .limit(10);

        if (error) throw error;

        const formattedData = (data || []).map(doc => ({
          id: doc.id,
          name: doc.name,
          type: doc.file_type,
          size: doc.file_size,
          created_date: doc.created_date,
          project_name: doc.projects?.name
        }));

        setDocuments(formattedData);
      } catch (error) {
        console.error('Failed to load documents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <FileText className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No documents yet</p>
        <p className="text-xs text-gray-500 mt-1">Upload files to spaces</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-500 text-white">{documents.length}</Badge>
          <span className="text-xs text-gray-600">recent files</span>
        </div>
      </div>

      {/* Documents list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {documents.map((doc) => {
            const fileConfig = getFileIcon(doc.type);
            const FileIcon = fileConfig.icon;

            return (
              <div
                key={doc.id}
                className="p-2 rounded-lg border bg-white hover:shadow-md cursor-pointer transition-all group"
              >
                <div className="flex items-start gap-2">
                  {/* File icon */}
                  <div className={cn("p-1.5 rounded-lg shrink-0", fileConfig.color)}>
                    <FileIcon className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {doc.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-500">
                        {formatFileSize(doc.size)}
                      </span>
                      {doc.project_name && (
                        <>
                          <span className="text-[10px] text-gray-300">•</span>
                          <span className="text-[10px] text-gray-500 truncate flex items-center gap-0.5">
                            <Folder className="h-2.5 w-2.5" />
                            {doc.project_name}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {formatDistanceToNow(parseISO(doc.created_date), { addSuffix: true })}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Eye className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Download className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/docs')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors shrink-0"
      >
        <FileText className="h-3.5 w-3.5" />
        <span>View All Documents</span>
      </button>
    </div>
  );
}
